import React, { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { Bell, ChevronRight } from "lucide-react";
import {
  useNotifications,
  useMarkAsRead,
} from "@/hooks/domains/notifications/useNotifications";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { Notification } from "@/types/notification";
import { getProfileUrl } from "@/utils/profileRedirects";

interface NotificationItemProps {
  notification: Notification;
  onActionClick: () => void;
}

interface NotificationResponse {
  data: Notification[];
  count: number;
}

const NotificationItem: React.FC<NotificationItemProps> = memo(
  ({ notification, onActionClick }) => {
    const getInitials = (name?: string | null) => {
      if (!name) return "U";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    };

    const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
      addSuffix: true,
    });

    // Determine icon and action based on notification type
    const getActionButton = () => {
      switch (notification.type) {
        case "portfolio_request":
          return (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs mt-1 text-primary"
              onClick={onActionClick}
            >
              Update Portfolio
            </Button>
          );
        case "experience_request":
          return (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs mt-1 text-primary"
              onClick={onActionClick}
            >
              Add Experience
            </Button>
          );
        case "like":
        case "view":
        case "profile_view":
          // Only show "View Profile" button for authenticated users (non-anonymous)
          if (notification.actor_id && !notification.is_anonymous) {
            return (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs mt-1 text-primary"
                onClick={onActionClick}
              >
                View Profile
              </Button>
            );
          }
          return null;
        default:
          return null;
      }
    };

    return (
      <div className={`p-4 ${!notification.read ? "bg-blue-50" : ""}`}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {notification.actor_avatar_url ? (
              <Avatar className="h-10 w-10">
                <AvatarImage
                  src={notification.actor_avatar_url}
                  alt={notification.actor_name || "User"}
                />
                <AvatarFallback>
                  {getInitials(notification.actor_name)}
                </AvatarFallback>
              </Avatar>
            ) : (
              <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                <Bell className="h-5 w-5 text-gray-500" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p
              className={`text-sm ${!notification.read ? "font-semibold" : ""}`}
            >
              {notification.message}
            </p>
            <p className="text-xs text-gray-500 mt-1">{timeAgo}</p>

            {getActionButton()}
          </div>
        </div>
      </div>
    );
  }
);

const NotificationsDropdown: React.FC = () => {
  const { user } = useAuth();
  const { data: notificationResponse, isLoading } = useNotifications(user?.id);
  const markAsReadMutation = useMarkAsRead();
  const navigate = useNavigate();

  // Extract the notifications array from the response object
  const notifications: Notification[] =
    (notificationResponse as NotificationResponse)?.data || [];

  const handleGoToNotifications = useCallback(() => {
    navigate("/notifications");
  }, [navigate]);

  const handleActionClick = useCallback(
    (notification: Notification) => {
      // Mark notification as read
      markAsReadMutation.mutate(notification.id);

      // Navigate based on notification type
      switch (notification.type) {
        case "portfolio_request":
          navigate("/dashboard/portfolio");
          break;
        case "experience_request":
          navigate("/dashboard/experience");
          break;
        case "like":
        case "view":
        case "profile_view":
          // Navigate to the actor's profile if they are authenticated
          if (notification.actor_id && !notification.is_anonymous) {
            const profileUrl = getProfileUrl(notification.actor_id);
            window.open(profileUrl, "_blank");
          } else {
            // Fallback to notifications page for anonymous views
            navigate("/notifications");
          }
          break;
        default:
          navigate("/notifications");
      }
    },
    [markAsReadMutation, navigate]
  );

  if (isLoading) {
    return (
      <div className="py-8 px-4 text-center">
        <div className="inline-block h-6 w-6 border-t-2 border-primary animate-spin rounded-full"></div>
        <p className="mt-2 text-sm text-gray-500">Loading notifications...</p>
      </div>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <div className="py-8 px-4 text-center">
        <p className="text-sm text-gray-500">No notifications yet</p>
      </div>
    );
  }

  // Limit to recent 3 notifications for dropdown
  const recentNotifications = notifications.slice(0, 3);

  return (
    <div className="max-h-[400px] overflow-auto">
      <div className="p-4 border-b border-gray-100">
        <h3 className="font-medium">Notifications</h3>
      </div>

      <div className="divide-y divide-gray-100">
        {recentNotifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onActionClick={() => handleActionClick(notification)}
          />
        ))}
      </div>

      <div className="p-3 border-t border-gray-100">
        <Button
          variant="outline"
          className="w-full text-sm"
          onClick={handleGoToNotifications}
        >
          View All Notifications
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default memo(NotificationsDropdown);
